import { useCallback } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { ImportDetail } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageTitle } from '../AppLayout'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { ContentWidth } from '../components/layout/ContentWidth'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import s from './ImportDetailPage.module.scss'

/**
 * Eine einzelne Lieferung — Kopf und der ANFANG ihrer Nutzlast.
 *
 * Nur der Anfang: Bei 1,4 MB CSV arbeitete der Browser sekundenlang für
 * Zeilen, die niemand liest. Wer alles braucht, hat den Spool des Collectors
 * — dort liegt dieselbe Datei vollständig, und der Kommentar dort nennt sie
 * ausdrücklich das Backup.
 */

const PERMISSION = 'import:read'

const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium', timeStyle: 'short',
})

function umfang(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', {
    minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`
}

export function ImportDetailPage() {
  const allowed = useHasPermission(PERMISSION)
  const { id = '' } = useParams()
  const location = useLocation()
  const zurueck = (location.state as { from?: string } | null)?.from ?? ''

  // `useCallback` mit `id` als Abhängigkeit — eine bei jedem Rendern neue
  // Ladefunktion bräche ihren eigenen Aufruf sofort wieder ab.
  const { item, loading, error, denied, notFound } = useItemQuery<ImportDetail>(
    useCallback((signal: AbortSignal) =>
      api.importDetail(Number(id), signal), [id]),
    allowed)

  usePageTitle(item ? `Lieferung ${item.id}` : 'Lieferung',
               item?.reference ?? undefined)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Importstrecke" />
  }

  return (
    <ContentWidth>
      <p className={s.zurueck}>
        <Link to={`/admin/imports${zurueck}`}>← zurück zur Liste</Link>
      </p>

      {loading && <Card span={12}><Loading>lädt Lieferung …</Loading></Card>}
      {error && !notFound && <ErrorBox>{error}</ErrorBox>}
      {notFound && (
        <Card span={12}>
          <EmptyState title="Lieferung nicht vorhanden">
            Zu dieser Kennung gibt es nichts. Möglicherweise ist sie
            inzwischen gelöscht worden.
          </EmptyState>
        </Card>
      )}

      {item && <Lieferung eintrag={item} />}
    </ContentWidth>
  )
}

function Lieferung({ eintrag }: { eintrag: ImportDetail }) {
  return (
    <>
      <Card title="Kopf" span={12}>
        <dl className={s.felder}>
          <Feld name="Art">{eintrag.data_type}</Feld>
          <Feld name="Quelle">{eintrag.source}</Feld>
          <Feld name="Format">{eintrag.content_type}</Feld>
          <Feld name="Referenz">
            {eintrag.reference ?? <span className={s.ohne}>keine</span>}
          </Feld>
          <Feld name="Angelegt">
            {zeitpunkt.format(new Date(eintrag.created))}
          </Feld>
          <Feld name="Umfang">{umfang(eintrag.payload_bytes)}</Feld>
          <Feld name="Aufgehoben bis">
            {eintrag.delete_at
              ? zeitpunkt.format(new Date(eintrag.delete_at))
              : <span className={s.ohne}>unbefristet</span>}
          </Feld>
        </dl>
      </Card>

      <Card title="Ableitung" span={12}
            meta={eintrag.derived_at
              ? zeitpunkt.format(new Date(eintrag.derived_at))
              : 'offen'}>
        {eintrag.derive_error ? (
          <p className={s.fehler}>{eintrag.derive_error}</p>
        ) : eintrag.derived_at ? (
          <p className={s.hinweis}>
            In die Zieltabellen übernommen.
          </p>
        ) : (
          <p className={s.hinweis}>
            Noch nicht übernommen. Für manche Art gibt es keinen Ableiter —
            die Lieferung liegt dann als Beleg und wartet auf einen.
          </p>
        )}
      </Card>

      <Card title="Nutzlast" span={12}
            meta={eintrag.payload_truncated
              ? `Anfang von ${umfang(eintrag.payload_bytes)}`
              : umfang(eintrag.payload_bytes)}>
        {eintrag.payload_truncated && (
          // DAZUGESAGT, nicht stillschweigend gekürzt: Sonst hält jemand
          // den Anfang für das Ganze und wundert sich über fehlende Zeilen.
          <p className={s.hinweis}>
            Gekürzt. Vollständig liegt die Lieferung im Spool des Collectors.
          </p>
        )}
        <pre className={s.nutzlast}>{eintrag.payload_head}</pre>
      </Card>
    </>
  )
}

function Feld({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className={s.feld}>
      <dt>{name}</dt>
      <dd>{children}</dd>
    </div>
  )
}
